import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Stack,
  Slider,
  Group,
  Button,
  Paper,
  SegmentedControl,
  TagsInput,
  Badge,
  Anchor,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { suggestionsApi } from '../api/suggestions';
import { DEFAULT_MATCH_CONFIG, type MatchConfig } from '../types';
import { pageContainer } from '../styles/theme.css';

const STORAGE_KEY = 'match_config';

function loadConfig(): MatchConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_MATCH_CONFIG, ...JSON.parse(stored) };
  } catch (_) {
    // ignore parse errors
  }
  return { ...DEFAULT_MATCH_CONFIG };
}

function saveConfig(config: MatchConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

function SliderRow({
  label,
  description,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        </div>
        <Badge variant="light" size="lg" w={40} style={{ textAlign: 'center' }}>
          {value}
        </Badge>
      </Group>
      <Slider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={1}
        marks={Array.from({ length: max - min + 1 }, (_, i) => ({ value: min + i }))}
        label={null}
      />
    </Stack>
  );
}

export function MatchSettings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [config, setConfig] = useState<MatchConfig>(loadConfig);
  const [lastResult, setLastResult] = useState<number | null>(null);

  function set<K extends keyof MatchConfig>(key: K, value: MatchConfig[K]) {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }

  const rematchMutation = useMutation({
    mutationFn: () => suggestionsApi.rematch(config),
    onSuccess: ({ created }) => {
      saveConfig(config);
      setLastResult(created);
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      notifications.show({
        message: `Matching kørt — ${created} nye forslag oprettet`,
        color: 'green',
      });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  function handleReset() {
    setConfig({ ...DEFAULT_MATCH_CONFIG });
    setLastResult(null);
  }

  return (
    <div className={pageContainer}>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>Match-indstillinger</Title>
            <Text size="sm" c="dimmed">
              Justér pointsystemet og kør matching mod alle eksisterende genstande og
              efterlysninger.{' '}
              <Anchor size="sm" onClick={() => navigate('/matches')}>
                Tilbage til match-oversigt
              </Anchor>
            </Text>
          </Stack>
          <Button variant="subtle" size="xs" onClick={handleReset}>
            Nulstil til standard
          </Button>
        </Group>

        {/* Threshold */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Grænseværdi</Text>
            <SliderRow
              label="Minimum score for forslag"
              description="Et par foreslås kun hvis scoren er over denne grænse. Hæv for færre, mere sikre forslag."
              value={config.threshold}
              min={1}
              max={12}
              onChange={(v) => set('threshold', v)}
            />
          </Stack>
        </Paper>

        {/* Color */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Farve</Text>
            <SliderRow
              label="Eksakt match"
              description='Point for identisk farve (f.eks. "sort" = "sort")'
              value={config.colorExact}
              min={0}
              max={8}
              onChange={(v) => set('colorExact', v)}
            />
            <SliderRow
              label="Delvist match"
              description='Point når én farve indeholder den anden (f.eks. "mørkeblå" indeholder "blå")'
              value={config.colorPartial}
              min={0}
              max={6}
              onChange={(v) => set('colorPartial', v)}
            />
          </Stack>
        </Paper>

        {/* Brand */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Mærke</Text>
            <SliderRow
              label="Eksakt match"
              description='Point for identisk mærke (f.eks. "Nike" = "Nike")'
              value={config.brandExact}
              min={0}
              max={8}
              onChange={(v) => set('brandExact', v)}
            />
            <SliderRow
              label="Delvist match"
              description="Point når ét mærke indeholder det andet"
              value={config.brandPartial}
              min={0}
              max={6}
              onChange={(v) => set('brandPartial', v)}
            />
          </Stack>
        </Paper>

        {/* Keywords */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Nøgleord</Text>
            <SliderRow
              label="Maks keyword point"
              description="Maksimalt antal point fra fælles keywords på tværs af titel, beskrivelse og kendetegn"
              value={config.keywordMax}
              min={0}
              max={10}
              onChange={(v) => set('keywordMax', v)}
            />
            <Stack gap={4}>
              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={500}>
                    Minimum ordlængde
                  </Text>
                  <Text size="xs" c="dimmed">
                    Kortere ord ignoreres for at undgå falske matches
                  </Text>
                </div>
                <Badge variant="light" size="lg">
                  {config.keywordMinLength}
                </Badge>
              </Group>
              <SegmentedControl
                value={String(config.keywordMinLength)}
                onChange={(v) => set('keywordMinLength', Number(v))}
                data={[
                  { label: '3 tegn', value: '3' },
                  { label: '4 tegn', value: '4' },
                  { label: '5 tegn', value: '5' },
                ]}
                size="xs"
              />
            </Stack>
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Stopord
              </Text>
              <Text size="xs" c="dimmed">
                Ord der ignoreres ved nøgleordssammenligning
              </Text>
              <TagsInput
                value={config.stopWords}
                onChange={(v) => set('stopWords', v)}
                placeholder="Tilføj stopord..."
                splitChars={[',', ' ']}
              />
            </Stack>
          </Stack>
        </Paper>

        {lastResult !== null && (
          <Alert color="green" variant="light">
            Sidst kørte matching oprettede <b>{lastResult}</b> nye forslag.
          </Alert>
        )}

        <Group>
          <Button
            size="md"
            onClick={() => rematchMutation.mutate()}
            loading={rematchMutation.isPending}
          >
            Kør matching nu
          </Button>
          <Text size="xs" c="dimmed" style={{ alignSelf: 'center' }}>
            Sletter alle ventende forslag og kører forfra. Bekræftede og afviste forslag bevares.
          </Text>
        </Group>
      </Stack>
    </div>
  );
}
